import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Empty, Icon, type ModalOptions, type RcCustomRequestOptions, Space, Spin, Text, Upload, message } from 'ui';
import { handleAxiosError } from 'common/error-handlers';
import { CreateFolderModal, type CreateFolderModalOptions } from '../../create-folder-modal';
import { useCreateAssetMutation } from '../../queries';

const { Dragger } = Upload;

type P = {
  canManage: boolean;
  communityId?: string;
  parentId?: string;
  programId?: string;
};

const AssetsTableEmptyState = ({ canManage, communityId, parentId, programId }: P) => {
  const { t } = useTranslation();
  const pendingUploads = useRef(0);
  const [isLoadingUploadFile, setIsLoadingUploadFile] = useState(false);
  const [createFolderOptions, setCreateFolderOptions] = useState<ModalOptions<CreateFolderModalOptions>>({
    isOpened: false,
  });
  const { mutateAsync: uploadFile } = useCreateAssetMutation();

  if (!canManage) {
    return <Empty description={{ title: 'No data' }} image={<Icon icon="inbox-line" />} />;
  }

  const customRequest = ({ file }: RcCustomRequestOptions) => {
    if (!(file instanceof File)) {
      message.error('Uploaded item is not a File object');
      return;
    }

    pendingUploads.current += 1;
    setIsLoadingUploadFile(true);

    void (async () => {
      try {
        const uploadedFile = await uploadFile({ communityId, file, parentId: parentId ?? null, programId });

        message.success(t('assetUploadSuccess', { asset: uploadedFile.name }));
      } catch (error) {
        handleAxiosError(error instanceof Error ? error : null, 'assetUploadFail');
      } finally {
        pendingUploads.current -= 1;

        if (pendingUploads.current === 0) {
          setIsLoadingUploadFile(false);
        }
      }
    })();
  };

  return (
    <>
      <Spin spinning={isLoadingUploadFile}>
        <Dragger customRequest={customRequest} disabled={isLoadingUploadFile} multiple showUploadList={false}>
          <Space block orientation="vertical" size="medium">
            <Icon color="primary" icon="inbox-line" size="large" />
            <Space block orientation="vertical" size="small">
              <Text size="large" type="title" weight="medium">
                Click or drag file to this area to upload
              </Text>
              <Text
                onClick={(event) => {
                  event.stopPropagation();
                  setCreateFolderOptions({ communityId, isOpened: true, parentId, programId });
                }}
                type="secondary"
              >
                Or create folder
              </Text>
            </Space>
          </Space>
        </Dragger>
      </Spin>
      <CreateFolderModal
        onCancel={() => {
          setCreateFolderOptions({ isOpened: false });
        }}
        options={createFolderOptions}
      />
    </>
  );
};

export default AssetsTableEmptyState;
